import { atom } from 'jotai';
import { atomFamily } from 'jotai/utils';
import type { WorkspaceProjection } from '@localagent/sdk';
import { configureAtomDevtools } from '../utils/tracking';

export interface WorkspaceProjectionState {
  projection: WorkspaceProjection | null;
  refreshRequested: boolean;
  revisionGap: { from: number; to: number } | null;
  refreshIndexRevision: number | null;
}

export type WorkspaceProjectionEvent =
  | { type: 'projection_received'; projection: WorkspaceProjection }
  | { type: 'projection_refetched'; projection: WorkspaceProjection }
  | {
      type: 'index_progress_received';
      workspaceId: string;
      emitterEpoch: number;
      indexRevision: number;
    };

const emptyProjectionState: WorkspaceProjectionState = {
  projection: null,
  refreshRequested: false,
  revisionGap: null,
  refreshIndexRevision: null,
};

export function reduceWorkspaceProjection(
  state: WorkspaceProjectionState,
  event: WorkspaceProjectionEvent
): WorkspaceProjectionState {
  if (event.type === 'index_progress_received') {
    if (!state.projection || state.projection.workspace_id !== event.workspaceId) return state;
    if (event.emitterEpoch !== state.projection.assignment.epoch) return state;
    const requestedRevision = state.refreshIndexRevision ?? state.projection.index.index_revision;
    if (event.indexRevision <= requestedRevision) return state;
    return {
      ...state,
      refreshRequested: true,
      refreshIndexRevision: event.indexRevision,
    };
  }

  const currentRevision = state.projection?.revision;
  const incomingRevision = event.projection.revision;

  if (currentRevision === undefined) {
    return {
      projection: event.projection,
      refreshRequested: false,
      revisionGap: null,
      refreshIndexRevision: null,
    };
  }

  if (incomingRevision <= currentRevision) {
    if (event.type === 'projection_refetched' && incomingRevision === currentRevision) {
      if (
        state.projection &&
        event.projection.index.index_revision < state.projection.index.index_revision
      ) {
        return state;
      }
      const indexSatisfied =
        state.refreshIndexRevision === null ||
        event.projection.index.index_revision >= state.refreshIndexRevision;
      return {
        ...state,
        projection: event.projection,
        refreshRequested: !indexSatisfied,
        revisionGap: null,
        refreshIndexRevision: indexSatisfied ? null : state.refreshIndexRevision,
      };
    }
    return state;
  }

  const gap = incomingRevision > currentRevision + 1;
  const indexSatisfied =
    state.refreshIndexRevision === null ||
    event.projection.index.index_revision >= state.refreshIndexRevision;
  const revisionGap =
    event.type === 'projection_received' && gap
      ? { from: currentRevision, to: incomingRevision }
      : null;
  return {
    projection: event.projection,
    refreshRequested: revisionGap !== null || !indexSatisfied,
    revisionGap,
    refreshIndexRevision: indexSatisfied ? null : state.refreshIndexRevision,
  };
}

export const workspaceProjectionsAtom = configureAtomDevtools(
  atom<Record<string, WorkspaceProjectionState>>({}),
  'workspace/projections'
);

export const workspaceProjectionAtomFamily = atomFamily((workspaceId: string) =>
  configureAtomDevtools(
    atom((get) => get(workspaceProjectionsAtom)[workspaceId] ?? emptyProjectionState),
    `workspace/projection/${workspaceId}`
  )
);

export const applyWorkspaceProjectionAction = configureAtomDevtools(
  atom<null, [WorkspaceProjectionEvent], void>(null, (get, set, event) => {
    const workspaceId =
      event.type === 'index_progress_received' ? event.workspaceId : event.projection.workspace_id;
    const current = get(workspaceProjectionsAtom)[workspaceId] ?? emptyProjectionState;
    const next = reduceWorkspaceProjection(current, event);
    if (next === current) return;

    set(workspaceProjectionsAtom, {
      ...get(workspaceProjectionsAtom),
      [workspaceId]: next,
    });
  }),
  'workspace/applyProjection'
);
